import * as React from 'react';

import type * as api from '@gitbook/api-client';
import {
    getAbsolutePagePathInRevision,
    isRevisionPageDocument,
    isRevisionPageLink,
    isRevisionPageParent,
    type Revision,
    RevisionPageType,
    type RevisionTreePage,
} from '@gitbook/hive-core';
import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    type BadgeProps,
    Button,
    ButtonGroup,
    Checkbox,
    Disclosure,
    DisclosureChevron,
    DisclosurePanel,
    DisclosureTrigger,
    Heading,
    Link,
} from '@gitbook/spine-react';
import type { DiffTreeChange } from '@gitbook/util-tree-merge';

import { CommentButton, CommentsScopedProvider, useContentReference } from '../..';
import { PageDocumentDiff, PageDocumentDiffSkeleton } from './PageDocumentDiff';
import { useViewedPageChange } from './viewedChanges';

/**
 * Display a diff panel for a page in a change request.
 */
export function PageDiffPanel(props: {
    organizationId: string;
    changeRequest: api.ChangeRequest;
    currentRevision: Revision;
    baseRevision: Revision;
    pageDiff: DiffTreeChange<RevisionTreePage>;

    /**
     * If true, the diff will be loaded by default; otherwise the user will need to click to generate it.
     */
    defaultGenerated?: boolean;
}) {
    const {
        organizationId,
        changeRequest,
        pageDiff,
        currentRevision,
        baseRevision,
        defaultGenerated = false,
    } = props;
    const page = pageDiff.item.page;

    const [generated, setGenerated] = React.useState(defaultGenerated);
    const [viewed, setViewed] = useViewedPageChange({
        changeRequestId: changeRequest.id,
        pageId: page.id,
    });

    const basePath = React.useMemo(() => {
        const basePage = baseRevision.pages[page.id];
        if (!basePage || !isRevisionPageParent(basePage)) {
            return null;
        }

        return getAbsolutePagePathInRevision(baseRevision, page.id);
    }, [baseRevision, page]);

    const currentPath = React.useMemo(() => {
        const currentPage = currentRevision.pages[page.id];
        if (!currentPage || !isRevisionPageParent(currentPage)) {
            return null;
        }

        return getAbsolutePagePathInRevision(currentRevision, page.id);
    }, [currentRevision, page]);

    const isPageLink = isRevisionPageLink(page);
    const contentRef = useContentReference(
        isPageLink ? (page.target as api.ContentRef) : undefined
    );

    const headerContent = (
        <div className="flex flex-1 flex-wrap items-center gap-2">
            <Badge {...getDiffBadgeProps(pageDiff)} size="xsmall" />
            <Heading level={3} className="text-secondary text-ui-action group-hover:text-base">
                {page.title}
            </Heading>
            {!isPageLink && currentPath && basePath !== currentPath ? (
                <div className="flex flex-row items-center gap-1 text-secondary text-ui-small">
                    {basePath ? (
                        <>
                            <span className="rounded border border-base bg-muted p-0.5 content-mono">
                                /{basePath}
                            </span>
                            <Icon.ArrowRight />
                        </>
                    ) : null}
                    <span className="rounded border border-base bg-muted p-0.5 content-mono">
                        /{currentPath}
                    </span>
                </div>
            ) : null}
        </div>
    );

    if (isPageLink) {
        return (
            <div className="flex w-full flex-col overflow-hidden rounded border border-base">
                <PanelDiffHeader>
                    <div className="flex items-center gap-2">
                        {headerContent}
                        <Button
                            onPress={() => contentRef.open()}
                            label={`Link to ${contentRef.title}`}
                            iconOnly
                            trailingIcon={Icon.PageLink}
                            kind="nested"
                            size="small"
                        />
                    </div>
                </PanelDiffHeader>
            </div>
        );
    }

    return (
        <CommentsScopedProvider pageId={page.id}>
            <Disclosure defaultExpanded>
                {({ expand, collapse }) => (
                    <div className="flex w-full flex-col rounded border border-base">
                        <PanelDiffHeader>
                            <DisclosureTrigger>
                                <Button
                                    leadingIcon={<DisclosureChevron />}
                                    iconOnly
                                    label="Toggle page"
                                    kind="nested"
                                />
                            </DisclosureTrigger>

                            {headerContent}

                            <ButtonGroup>
                                <Button
                                    kind="nested"
                                    label="Viewed"
                                    leadingIcon={<Checkbox value={!!viewed} readOnly />}
                                    onPress={() => {
                                        setViewed(
                                            viewed
                                                ? false
                                                : {
                                                      documentId: isRevisionPageDocument(page)
                                                          ? (page.document ?? null)
                                                          : null,
                                                  }
                                        );
                                        if (viewed) {
                                            expand();
                                        } else {
                                            collapse();
                                        }
                                    }}
                                />

                                {typeof currentPath === 'string' ? (
                                    <Button
                                        kind="nested"
                                        label="View & edit in change request"
                                        iconOnly
                                        trailingIcon={Icon.Edit}
                                        href={{
                                            screen: {
                                                type: 'space',
                                                organization: organizationId,
                                                space: currentRevision.contentId,
                                                changeRequest: changeRequest.number,
                                                page: currentPath,
                                            },
                                        }}
                                    />
                                ) : null}

                                {page.type === RevisionPageType.Document ? (
                                    <CommentButton
                                        kind="nested"
                                        size="medium"
                                        label="Comment on this page"
                                        shortenLabel
                                        iconOnly
                                        leadingIcon={Icon.Comment}
                                        nodeKey={undefined}
                                        intent="create" // To ensure it always open as a popover
                                    />
                                ) : null}
                            </ButtonGroup>
                        </PanelDiffHeader>
                        <DisclosurePanel>
                            <div className="px-12 py-4">
                                {isMovedWithoutChangePageDocument(pageDiff) ? (
                                    <p className="text-secondary text-ui-base">
                                        This page has been moved without any changes.
                                    </p>
                                ) : pageDiff.type === 'deleted' && !generated ? (
                                    <p className="text-secondary text-ui-base">
                                        This page has been deleted.{' '}
                                        <Link onPress={() => setGenerated(true)}>
                                            See deleted content
                                        </Link>
                                        .
                                    </p>
                                ) : generated ? (
                                    <React.Suspense fallback={<PageDocumentDiffSkeleton />}>
                                        <PageDocumentDiff {...props} />
                                    </React.Suspense>
                                ) : (
                                    <div className="relative">
                                        <PageDocumentDiffSkeleton />
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <Button
                                                kind="nested"
                                                label="Generate diff"
                                                onPress={() => setGenerated(true)}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </DisclosurePanel>
                    </div>
                )}
            </Disclosure>
        </CommentsScopedProvider>
    );
}

/**
 * Skeleton for the page diff panel.
 */
export function PageDiffPanelSkeleton() {
    return (
        <div className="rounded border border-base p-6">
            <PageDocumentDiffSkeleton />
        </div>
    );
}

function isMovedWithoutChangePageDocument(pageDiff: DiffTreeChange<RevisionTreePage>) {
    if (pageDiff.type !== 'modified') {
        return false;
    }

    if (
        pageDiff.before.page.type !== RevisionPageType.Document ||
        pageDiff.item.page.type !== RevisionPageType.Document
    ) {
        return false;
    }

    return pageDiff.before.page.document === pageDiff.item.page.document;
}

function getDiffBadgeProps(pageDiff: DiffTreeChange<RevisionTreePage>): BadgeProps {
    if (isMovedWithoutChangePageDocument(pageDiff)) {
        return {
            kind: 'warning',
            label: 'Moved',
        };
    }

    if (pageDiff.type === 'deleted') {
        return {
            kind: 'danger',
            label: 'Deleted',
        };
    }

    if (pageDiff.type === 'added') {
        return {
            kind: 'success',
            label: 'Added',
        };
    }

    return {
        kind: 'warning',
        label: 'Modified',
    };
}

function PanelDiffHeader(props: { children: React.ReactNode }) {
    const { children } = props;
    return (
        <div className="group sticky top-0 z-10 mb-[-1px] flex w-full items-start gap-3 rounded-t border-b border-b-base border-solid bg-base bg-standout-base px-3 py-2 text-left focus-visible:shadow-focus-ring-inset">
            <div className="flex flex-1 items-center gap-2">{children}</div>
        </div>
    );
}
